/**
 * Partner vouchers (#2357). A partner creates a voucher through hellohousing's partner API
 * (b2b-api) and hands the customer a link, e.g. https://app.hellohousing.de/redeem?code=Q1EQ-0GI3-LIHE-WTJB.
 * The redeem page (routes/Redeem.jsx) checks the code, then redeems it once the customer has
 * signed in or created an account.
 *
 * Endpoints, as tested on staging on 2026-10-27 (all in hellohousing's { status, message, result }):
 *  - GET  {config.vouchers}?code=            public -> { id, code, description, status, subscriptionId, redeemedOn }
 *                                            status 'Active' / 'Cancelled' (seen); unknown code: 400 NOT_FOUND
 *  - GET  {config.vouchers}/{id}/subscription public -> the plan: { id, name, category, description, billing, … },
 *                                            or result null when the plan doesn't exist
 *  - POST {config.accountVouchers}{id}/redeem signed in (Authorization) -> redeems it. Only for an
 *                                            account with the voucher's partnerId (set at signup, see
 *                                            createAccount); any other answers 400 NOT_FOUND
 * The plan carries the partner (`partnerId`, since 2026-10-28). Expiry date and term aren't sent yet
 * (asked the backend); they're used as soon as they come (`expiresOn`, the plan's `termPeriod`).
 * Errors are mapped by HTTP status first and the message second, so a code redeemed in another
 * tab meanwhile still reads as "already redeemed" once the backend sends such a message.
 */
import moment from 'moment';
import APIClient from '../neoverv/config/APIClient';
import { config } from '../neoverv/config/Setting';

// What the redeem page shows for a code.
export const VOUCHER_STATE = {
  VALID: 'VALID',
  MISSING: 'MISSING', // the link has no code
  INVALID: 'INVALID', // unknown or malformed code, or one whose plan doesn't exist
  REDEEMED: 'REDEEMED',
  EXPIRED: 'EXPIRED',
  CANCELLED: 'CANCELLED', // cancelled by the partner
  OTHER_PARTNER: 'OTHER_PARTNER', // the signed-in account wasn't created through the voucher's partner
  RATE_LIMITED: 'RATE_LIMITED',
  SIGNED_OUT: 'SIGNED_OUT', // redeeming with a token that's no longer valid
  ERROR: 'ERROR', // network or server error, worth retrying
};

// Codes are typed or pasted by people: "q1eq-0gi3 -lihe-wtjb " is Q1EQ-0GI3-LIHE-WTJB.
export const normalizeVoucherCode = (code) => (code ? code.toString().replace(/\s+/g, '').toUpperCase() : '');

const STATE_BY_STATUS = {
  ACTIVE: VOUCHER_STATE.VALID,
  REDEEMED: VOUCHER_STATE.REDEEMED,
  EXPIRED: VOUCHER_STATE.EXPIRED,
  CANCELLED: VOUCHER_STATE.CANCELLED,
};

// State of a code from the check's result. A redeemed date or an expiry in the past wins over an
// Active status the backend hasn't updated yet.
export const getVoucherState = (voucher, now = moment()) => {
  const state = STATE_BY_STATUS[voucher?.status?.toString().toUpperCase()];
  if (!state) {
    return VOUCHER_STATE.INVALID;
  }
  if (state === VOUCHER_STATE.VALID && voucher.redeemedOn) {
    return VOUCHER_STATE.REDEEMED;
  }
  if (state === VOUCHER_STATE.VALID && voucher.expiresAt && moment(voucher.expiresAt).isBefore(now)) {
    return VOUCHER_STATE.EXPIRED;
  }
  return state;
};

const STATE_BY_HTTP_STATUS = {
  401: VOUCHER_STATE.SIGNED_OUT,
  404: VOUCHER_STATE.INVALID,
  409: VOUCHER_STATE.REDEEMED,
  410: VOUCHER_STATE.EXPIRED,
  429: VOUCHER_STATE.RATE_LIMITED,
};

// State of a failed call: an axios error, or the body of a 200 that says { status: false }.
// hellohousing answers most refusals with 400 and a message, so for 400 the message decides.
export const getVoucherErrorState = ({ httpStatus, message } = {}) => {
  if (STATE_BY_HTTP_STATUS[httpStatus]) {
    return STATE_BY_HTTP_STATUS[httpStatus];
  }
  const text = message?.toString().toUpperCase() || '';
  if (text.includes('REDEEM')) return VOUCHER_STATE.REDEEMED;
  if (text.includes('EXPIRE')) return VOUCHER_STATE.EXPIRED;
  if (text.includes('CANCEL')) return VOUCHER_STATE.CANCELLED;
  if (text.includes('NOT_FOUND') || text.includes('INVALID') || httpStatus === 400) return VOUCHER_STATE.INVALID;
  return VOUCHER_STATE.ERROR;
};

const errorDetails = (error) => ({
  httpStatus: error?.response?.status,
  message: error?.response?.data?.message || error?.response?.data?.code,
});

// hellohousing wraps results as { status, message, result }; a bare object is taken as the result.
const unwrap = (data) => {
  if (data && typeof data === 'object' && 'status' in data && 'result' in data) {
    return data.status === false ? { failed: true, message: data.message || data.code } : { result: data.result };
  }
  return { result: data };
};

// -> { result } or { state } of a failed call.
const request = async (requestConfig) => {
  try {
    const { failed, message, result } = unwrap((await APIClient(requestConfig)).data);
    return failed ? { state: getVoucherErrorState({ message }) } : { result };
  } catch (error) {
    return { state: getVoucherErrorState(errorDetails(error)) };
  }
};

// The voucher as the redeem page reads it: the check's fields, its plan as `subscriptionType`, and
// partner and expiry from whichever of the two answers carries them.
const toVoucher = (voucher, plan) => ({
  ...voucher,
  partnerId: voucher.partnerId || plan?.partnerId || null,
  expiresAt: voucher.expiresOn || voucher.expiresAt || null,
  subscriptionType: plan || null,
});

// -> { state, voucher }. `voucher` is set for every code that exists, also one that can't be redeemed.
export const validateVoucher = async (code) => {
  const normalizedCode = normalizeVoucherCode(code);
  if (!normalizedCode) {
    return { state: VOUCHER_STATE.MISSING, voucher: null };
  }
  const check = await request({ method: 'GET', url: config.vouchers, params: { code: normalizedCode } });
  if (check.state || !check.result?.id) {
    return { state: check.state || VOUCHER_STATE.INVALID, voucher: null };
  }
  const plan = await request({ method: 'GET', url: `${config.vouchers}/${check.result.id}/subscription` });
  const voucher = toVoucher(check.result, plan.result);
  const state = getVoucherState(voucher);
  if (state !== VOUCHER_STATE.VALID) {
    return { state, voucher };
  }
  // Redeemable only with a plan to show and assign.
  if (plan.state) {
    return { state: plan.state === VOUCHER_STATE.SIGNED_OUT ? VOUCHER_STATE.ERROR : plan.state, voucher };
  }
  return { state: plan.result ? VOUCHER_STATE.VALID : VOUCHER_STATE.INVALID, voucher };
};

const sameId = (a, b) => Boolean(a && b) && a.toString().toLowerCase() === b.toString().toLowerCase();
const isAssignment = (value) => Boolean(value && typeof value === 'object' && (value.nextRenewalDate || value.subscriptionType));

// The account's assignment of the voucher's plan, for the confirmation's dates: the active one
// that started last. Null when it can't be found.
export const findAssignment = async ({ accountId, authToken, subscriptionId }) => {
  const { result } = await request({
    method: 'GET',
    url: config.subscriptionAssignments,
    params: { accountId },
    headers: { Authorization: authToken },
  });
  const matching = (Array.isArray(result) ? result : [])
    .filter((assignment) => assignment?.subscriptionStatus === 'ACTIVE' && sameId(assignment?.subscriptionType?.subscriptionTypeId, subscriptionId));
  // The latest start runs longest: a partner plan's term counts from its start (getPrepaidTermEnd).
  return matching.sort((a, b) => moment(b.startDate).diff(moment(a.startDate)))[0] || null;
};

// After a signup whose activation redeemed the voucher: its assignment, once signed in. -> the
// assignment, or null when the activation didn't assign the plan (then redeemVoucher does).
export const findRedeemedAssignment = ({ voucher, accountId, authToken }) => findAssignment({ accountId, authToken, subscriptionId: voucher.subscriptionId });

// Redeems the voucher for the account the token belongs to. -> { assignment } or { state }.
// `assignment` is the redeem's answer when it is one, else looked up; it can be null.
// A partner's voucher goes only to an account of that partner, which the backend refuses as
// NOT_FOUND; checked first so the page can say why. If the account can't be read, the redeem decides.
export const redeemVoucher = async ({ voucher, accountId, authToken }) => {
  if (voucher.partnerId) {
    const account = await request({ method: 'GET', url: `${config.accounts}${accountId}`, headers: { Authorization: authToken } });
    if (account.state === VOUCHER_STATE.SIGNED_OUT) {
      return { state: VOUCHER_STATE.SIGNED_OUT };
    }
    if (account.result && !sameId(account.result.partnerId, voucher.partnerId)) {
      return { state: VOUCHER_STATE.OTHER_PARTNER };
    }
  }
  const redeemed = await request({
    method: 'POST',
    url: `${config.accountVouchers}${voucher.id}/redeem`,
    headers: { Authorization: authToken },
  });
  if (redeemed.state) {
    return { state: redeemed.state };
  }
  if (isAssignment(redeemed.result)) {
    return { assignment: redeemed.result };
  }
  return { assignment: await findAssignment({ accountId, authToken, subscriptionId: voucher.subscriptionId }) };
};
